import useSWR from 'swr';

import recipesService, { defaultSummaryRequestBody } from 'services/recipes';
import type { RecipeFilter, SummaryPreferences, SummaryResponse } from 'services/recipes/types';
import { useSearchParams } from 'renderer/hooks/useSearchParams';
import { queryToFilters } from 'renderer/utils/queryToFilters';
import {
  RECIPE_FEATURED_LAST_PUBLISHED,
  RECIPE_QUERY_PAGE,
  RECIPE_QUERY_SEARCH_TERM,
  RECIPE_QUERY_SORT_BY,
} from '../constants';
import RecipeListingPageLayout from '../RecipeListingPageLayout';

// placeholders for now
import RecipeListing from '../RecipeList';
const Filters = ({ loading }: { loading: boolean }) => (loading ? null : <div />);
const SortBy = ({ loading }: { loading: boolean }) => (loading ? null : <div />);

const fallbackData: SummaryResponse = { recipes: [], filters: [], totalMatches: 0 };

type RecipeListingPageProps = {
  dietaryFilters: RecipeFilter[];
  disableFilters: boolean;
  disableSortBy: boolean;
  gridSubtitle: string;
  gridTitle: string;
  gridType: 'recipe-cards' | 'featured';
  defaultSortBy: typeof RECIPE_FEATURED_LAST_PUBLISHED;
  preloadImages: boolean;
};

const RecipeListingPage = (props: RecipeListingPageProps) => {
  const [searchParams] = useSearchParams();

  const searchTerm = searchParams.get(RECIPE_QUERY_SEARCH_TERM) ?? '';
  const sortBy = searchParams.get(RECIPE_QUERY_SORT_BY) ?? '';
  const pageNumber = Number(searchParams.get(RECIPE_QUERY_PAGE) ?? 1) || 1;

  const filters = queryToFilters(searchParams);

  const fetchRecipesSummary = (): Promise<SummaryResponse> => {
    const size = defaultSummaryRequestBody.preferences.size;
    const start = (pageNumber - 1) * size;

    const preferences: SummaryPreferences = {
      ...defaultSummaryRequestBody.preferences,
      start,
      ...(sortBy && { sortBy }),
    };

    return recipesService.postSummary({
      body: {
        ...(searchTerm && { search: { term: searchTerm, tags: [[]] } }),
        ...(filters.length && { filters }),
        preferences,
      },
    });
  };

  const { data, error, isLoading } = useSWR(
    ['recipes/summary', searchTerm, sortBy, pageNumber, filters],
    fetchRecipesSummary,
    {
      fallbackData,
    },
  );

  const { recipes } = data;

  return (
    <RecipeListingPageLayout
      error={error}
      gridType={props.gridType}
      disableFilters={props.disableFilters}
      disableSortBy={props.disableSortBy}
      gridTitle={props.gridTitle}
      gridSubtitle={props.gridSubtitle}
      preloadImages={props.preloadImages}
      filterComponent={<Filters loading={isLoading} />}
      sortByComponent={<SortBy loading={isLoading} />}
      recipeCardGrid={<RecipeListing recipes={recipes} />}
    />
  );
};

export default RecipeListingPage;
